import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BadgeDollarSign, Calendar, Clock, FileText, Plus, Search } from 'lucide-react';

async function fetchInvoices(): Promise<Tables<'invoice_summary'>[]> {
  const { data, error } = await supabase
    .from('invoice_summary')
    .select('*')
    .order('invoice_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

const InvoicesPage: React.FC = () => {
  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: fetchInvoices
  });

  if (isLoading) {
    return (
      <PageContainer title="Invoices" subtitle="Manage your invoices">
        <div className="text-center text-muted-foreground">Loading invoices...</div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Invoices" subtitle="Manage your invoices">
        <div className="text-center text-destructive">Error loading invoices.</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Invoices" subtitle="Manage your invoices">
      <div className="space-y-4">
        {/* Search and actions bar */}
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              id="invoice-search"
              placeholder="Search invoices..." 
              className="pl-9"
            />
          </div>
          <Button size="icon" className="flex-shrink-0 animate-pulse-subtle">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Invoices list */}
        <div className="space-y-3 pb-20">
          {invoices.map((invoice, index) => (
            <Card 
              key={invoice.invoice_id}
              className="p-4 hover-lift touch-feedback active:scale-98 animate-slide-in-bottom"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-3">
                      <BadgeDollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium">{invoice.client_name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">#{invoice.invoice_number}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-end text-sm">
                  <div className="font-medium text-lg">
                    ${(invoice.total_amount || 0).toLocaleString()}
                  </div>
                  <div className={`text-xs mt-1 px-2 py-1 rounded-full ${
                    invoice.status_name?.toLowerCase() === 'paid' ? 'bg-green-100 text-green-800' :
                    invoice.status_name?.toLowerCase() === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {invoice.status_name}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 mr-1.5" />
                  <span>Due {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </PageContainer>
  );
};

export default InvoicesPage;
